import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { planService, companyService } from '../../api/services';

type PlanKind = 'STARTER' | 'PRO' | 'BUSINESS';

const PLAN_FEATURES: Record<PlanKind, { price: string; features: Record<string, string | boolean | number> }> = {
  STARTER: {
    price: '29 TND / mois',
    features: {
      'Factures / mois': 50,
      Utilisateurs: 1,
      Sociétés: 1,
      'PDF français': true,
      'PDF arabe': false,
      'Relances auto': false,
      'Export comptable': false,
      'Clés API': false,
      'Multi-sociétés': false,
      OCR: false,
    },
  },
  PRO: {
    price: '89 TND / mois',
    features: {
      'Factures / mois': 'Illimité',
      Utilisateurs: 5,
      Sociétés: 3,
      'PDF français': true,
      'PDF arabe': true,
      'Relances auto': true,
      'Export comptable': true,
      'Clés API': false,
      'Multi-sociétés': false,
      OCR: false,
    },
  },
  BUSINESS: {
    price: '199 TND / mois',
    features: {
      'Factures / mois': 'Illimité',
      Utilisateurs: 'Illimité',
      Sociétés: 10,
      'PDF français': true,
      'PDF arabe': true,
      'Relances auto': true,
      'Export comptable': true,
      'Clés API': true,
      'Multi-sociétés': true,
      OCR: true,
    },
  },
};

const Plan: React.FC = () => {
  const [current, setCurrent] = useState<PlanKind>('STARTER');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    companyService.get()
      .then((c: any) => c?.plan && setCurrent(c.plan))
      .catch(() => { /* ignore */ });
  }, []);

  const upgrade = async (plan: PlanKind) => {
    if (plan === current) return;
    setBusy(true);
    try {
      await planService.update(plan);
      setCurrent(plan);
      toast.success(`Plan changé vers ${plan}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors du changement de plan');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Votre plan</h2>
      <p className="text-sm text-gray-500">Plan actuel&nbsp;: <strong>{current}</strong>. Vous pouvez changer à tout moment, la facturation sera proratisée.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(Object.keys(PLAN_FEATURES) as PlanKind[]).map((plan) => {
          const info = PLAN_FEATURES[plan];
          const isActive = current === plan;
          return (
            <div key={plan}
                 className={`rounded-2xl border p-6 ${isActive ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold">{plan}</h3>
                {isActive && <span className="text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">Actuel</span>}
              </div>
              <p className="text-sm text-gray-500 mb-4">{info.price}</p>
              <ul className="text-sm space-y-1 mb-4">
                {Object.entries(info.features).map(([label, v]) => (
                  <li key={label} className="flex justify-between">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-medium">
                      {typeof v === 'boolean' ? (v ? '✓' : '—') : String(v)}
                    </span>
                  </li>
                ))}
              </ul>
              <button disabled={busy || isActive} onClick={() => upgrade(plan)}
                      className={`w-full py-2 rounded-lg font-semibold ${
                        isActive
                          ? 'bg-gray-100 text-gray-400 cursor-default'
                          : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                      }`}>
                {isActive ? 'Plan actuel' : `Passer en ${plan}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Plan;
